/* eslint-disable react/jsx-no-constructed-context-values */
import React, { createContext, useCallback, useState, useContext } from 'react';

import IProduct, { IProductPizzaSize } from '../models/IProduct';
import { IProductListContext } from '../models/ICartItemAnalytics';

import api from '../services/api';
import { normalizeProductChannelPrices } from '../utils/product-price';

interface ProductsContextData {
  loading: boolean;
  products: IProduct[];
  highlights: IProduct[];
  topProducts: IProduct[];
  discountProducts: IProduct[];
  selectedProduct?: number | null;
  /** List the current selection came from, when it came from one. */
  selectedProductList?: IProductListContext;
  selectedPizzaCategory?: number | null;
  selectedPizzaSize?: IProductPizzaSize | null;
  setSelectedProduct: (productId: number | null, list?: IProductListContext) => void;
  setSelectedPizzaCategory: (categoryId: number | null) => void;
  setSelectedPizzaSize: (size: IProductPizzaSize | null) => void;
  loadProducts(table: boolean, category?: number): Promise<void>;
  loadHighlights(table: boolean): Promise<void>;
}

const ProductsContext = createContext<ProductsContextData>({} as ProductsContextData);

interface IProductsProvider {
  children: React.ReactNode;
}

const getMetaContent = (name: string): string | null => {
  const meta = document.querySelector(`meta[name="${name}"]`);
  return meta ? meta.getAttribute('content') : null;
};

export const ProductsProvider: React.FC<IProductsProvider> = ({ children }) => {
  const [data, setData] = useState<IProduct[]>([]);
  const [highlights, setHighlights] = useState<IProduct[]>([]);

  const [topProducts, setTopProducts] = useState<IProduct[]>([]);
  const [discountProducts, setDiscountProducts] = useState<IProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<number | null>(null);
  const [selectedProductList, setSelectedProductList] = useState<IProductListContext | undefined>();
  const [selectedPizzaCategory, setSelectedPizzaCategory] = useState<number | null>(null);
  const [selectedPizzaSize, setSelectedPizzaSize] = useState<IProductPizzaSize | null>(null);

  const loadHighlights = useCallback(async (table: boolean) => {
    let response: IProduct[] = [];

    try {
      const modules = table ? 'TABLE' : 'DELIVERY';
      const apiResponse = await api.get<IProduct[]>('/products/highlights', {
        params: { modules },
      });
      response = (apiResponse.data || []).map((item) => normalizeProductChannelPrices(item, table));
    } finally {
      setHighlights(response || []);
    }
  }, []);

  const loadProducts = useCallback(async (table: boolean, category?: number) => {
    const foundCompany = getMetaContent('bsfood-found-company') !== 'false';

    if (foundCompany) {
      try {
        setLoading(true);
        const modules = table ? 'TABLE' : 'DELIVERY';

        const response = await api.get<IProduct[]>('/products', {
          params: {
            active: true,
            modules,
            ...(category && category !== -2 && { categoryId: category }),
          },
        });

        const top: IProduct[] = [];
        const normal: IProduct[] = [];
        const discount: IProduct[] = [];

        response.data.forEach((item) => {
          const normalizedItem: typeof item = {
            ...normalizeProductChannelPrices(item, table),
            complementsGroups: item.complementsGroups.filter(
              (group) =>
                group?.complements?.length > 0 &&
                (group?.modules ? group?.modules?.includes(table ? 'TABLE' : 'DELIVERY') : true),
            ),
          };

          if (normalizedItem.categories.map((pCategory) => pCategory.id).includes(-1)) {
            top.push(normalizedItem);
            normal.push({
              ...normalizedItem,
              categories: normalizedItem.categories.filter((c) => c.id !== -1),
            });
          } else {
            normal.push(normalizedItem);
          }

          if (normalizedItem.salePrice) {
            discount.push(normalizedItem);
          }
        });

        setData(normal);
        setDiscountProducts(discount);
        setTopProducts(top);
        setLoading(false);
      } catch (err) {
        console.log(err);
      }
    }
  }, []);

  const handleSetSelectedProduct = useCallback(
    (product: number | null, list?: IProductListContext) => {
      setSelectedProduct(product);
      // The context always follows the selection, so closing the detail or opening a product with
      // no list behind it (deep link, cart edit) clears it instead of leaking into the next item.
      setSelectedProductList(list);
    },
    [],
  );

  const handleSetSelectedPizzaCategory = (id: number | null) => setSelectedPizzaCategory(id);

  const handleSetSelectedPizzaSize = (size: IProductPizzaSize | null) => setSelectedPizzaSize(size);

  return (
    <ProductsContext.Provider
      value={{
        loading,
        products: data,
        highlights,
        topProducts,
        loadProducts,
        loadHighlights,
        selectedProduct,
        selectedProductList,
        discountProducts,
        selectedPizzaCategory,
        selectedPizzaSize,
        setSelectedProduct: handleSetSelectedProduct,
        setSelectedPizzaCategory: handleSetSelectedPizzaCategory,
        setSelectedPizzaSize: handleSetSelectedPizzaSize,
      }}
    >
      {children}
    </ProductsContext.Provider>
  );
};

export function useProducts(): ProductsContextData {
  const context = useContext(ProductsContext);

  if (!context) {
    throw new Error('useProducts must be used within ProductsProvider');
  }

  return context;
}
